// ---------------------------------------------------------------------------
// EmailCapture — "stay informed" signup on the landing page. Writes to the
// insert-only public.email_signups table (see the migration). A duplicate
// address is treated as success ("you're already on the list"), and any
// backend hiccup fails gently — this is a nice-to-have, never a blocker.
// ---------------------------------------------------------------------------

import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const EmailCapture = ({ source = "landing" }: { source?: string }) => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<"idle" | "loading" | "done" | "error">("idle");

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (state === "loading") return;
    const value = email.trim();
    if (!EMAIL_RE.test(value)) { setState("error"); return; }
    setState("loading");
    // The generated DB types don't include email_signups (added in a later
    // migration), so cast to reach it. Insert-only table; see migration.
    const { error } = await (supabase as unknown as {
      from: (t: string) => { insert: (v: unknown) => Promise<{ error: { code?: string } | null }> };
    }).from("email_signups").insert({ email: value, source });
    // 23505 = unique violation → already signed up, which is a success here.
    if (!error || error.code === "23505") {
      setState("done");
      // Send the welcome email only for a genuinely new signup (not a repeat).
      // Fire-and-forget: a mail hiccup must never undo the "you're on the
      // list" success the user just saw.
      if (!error) {
        void supabase.functions
          .invoke("signup-welcome", { body: { email: value, source } })
          .catch(() => {});
      }
    } else {
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <p className="text-[15px] text-primary font-medium">
        You're on the list. We'll let you know the moment it's ready.
      </p>
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col sm:flex-row gap-2 max-w-sm mx-auto">
      <input
        type="email"
        inputMode="email"
        autoComplete="email"
        placeholder="you@email.com"
        value={email}
        onChange={(e) => { setEmail(e.target.value); if (state === "error") setState("idle"); }}
        aria-label="Email address"
        aria-invalid={state === "error"}
        className="flex-1 h-11 rounded-full border border-border bg-background px-4 text-[15px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
      />
      <button
        type="submit"
        disabled={state === "loading"}
        className="h-11 px-5 rounded-full bg-primary text-primary-foreground text-[15px] font-medium hover:brightness-110 transition-smooth disabled:opacity-60 whitespace-nowrap"
      >
        {state === "loading" ? "…" : "Keep me posted"}
      </button>
      {state === "error" && (
        <span className="sr-only" role="alert">Please enter a valid email.</span>
      )}
    </form>
  );
};
